import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, FileText, Check, X, ShieldAlert, Megaphone, ClipboardList, ArrowUpRight, ExternalLink, User, Eye } from "lucide-react";
import SheetPreviewModal from "@/components/SheetPreviewModal";

const KIND = {
  "result.submit":     { label: "Result submitted",  color: "#5AC8FA", icon: ClipboardList },
  "result.approve":    { label: "Result approved",   color: "#34C759", icon: Check },
  "result.reject":     { label: "Result rejected",   color: "#FF453A", icon: X },
  "incident.create":   { label: "Incident filed",    color: "#FF9F0A", icon: ShieldAlert },
  "incident.status":   { label: "Incident updated",  color: "#FF9F0A", icon: ShieldAlert },
  "broadcast.sms":     { label: "SMS broadcast",     color: "#AF52DE", icon: Megaphone },
};

function fmtTime(iso) { return (iso || "").replace("T", " ").slice(0, 19); }

export default function Timeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [elections, setElections] = useState([]);
  const [electionId, setElectionId] = useState("");
  const [live, setLive] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    api.get("/elections").then(({ data }) => {
      setElections(data);
      const active = data.find(e => e.status === "active") || data[0];
      if (active) setElectionId(active.id);
    });
  }, []);

  // Live WS subscription — prepend events as they land
  useEffect(() => {
    if (!electionId) return;
    const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/collation/${electionId}?token=${encodeURIComponent(localStorage.getItem("ec_token") || "")}`;
    let ws;
    try {
      ws = new WebSocket(url);
      ws.onopen = () => setLive(true);
      ws.onclose = () => setLive(false);
      ws.onmessage = (ev) => {
        let m = null; try { m = JSON.parse(ev.data); } catch { return; }
        const kind = m.type === "result.approve" ? "result.approve"
                   : m.type === "result.reject" ? "result.reject"
                   : m.type === "result.submit" ? "result.submit"
                   : m.type === "incident.create" ? "incident.create"
                   : m.type === "broadcast.sms" ? "broadcast.sms" : null;
        if (!kind) return;
        setEvents(prev => [{
          at: new Date().toISOString(),
          kind, title: KIND[kind]?.label || kind,
          actor: m.by_email || m.reporter_email || "",
          meta: { polling_unit: m.polling_unit_name, severity: m.severity, message: m.message },
          target_id: m.result_id || m.incident_id || m.broadcast_id || `live-${Date.now()}`,
        }, ...prev].slice(0, 500));
      };
    } catch {}
    return () => { try { ws && ws.close(); } catch {} };
  }, [electionId]);

  const load = useCallback(async () => {
    if (!electionId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/timeline", { params: { election_id: electionId, limit: 500 }});
      setEvents(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [electionId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    return events.filter(e => e.kind === filter || (filter === "results" && e.kind?.startsWith("result.")) || (filter === "incidents" && e.kind?.startsWith("incident.")));
  }, [events, filter]);

  // Group by hour bucket for the horizontal ribbon
  const buckets = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const key = (e.at || "").slice(0, 13); // yyyy-mm-ddThh
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return Array.from(map.entries()).sort((a,b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px]">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Election Day</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="timeline-title">Timeline</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">Every transmission, approval, incident and broadcast on one horizontal ribbon — scroll left to replay the day.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={electionId} onChange={e=>setElectionId(e.target.value)} data-testid="timeline-election-select"
            className="bg-transparent border border-white/20 px-3 py-2 text-xs outline-none">
            {elections.map(e => <option key={e.id} value={e.id} className="bg-black">{e.name}</option>)}
          </select>
          <div className="flex border border-white/20">
            {[["all","All"],["results","Results"],["incidents","Incidents"],["broadcast.sms","Broadcasts"]].map(([k,l]) => (
              <button key={k} onClick={()=>setFilter(k)} data-testid={`timeline-filter-${k}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] ${filter===k?"bg-white text-black":"hover:bg-white/10"}`}>{l}</button>
            ))}
          </div>
          <button onClick={load} data-testid="timeline-refresh" className="p-2 border border-white/20 hover:bg-white/10">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
          </button>
          <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${live ? "border-emerald-500/40 text-emerald-300" : "border-white/20 text-zinc-500"}`} data-testid="timeline-live-dot">
            <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`}/> {live ? "LIVE" : "OFFLINE"}
          </span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
        {Object.entries(KIND).map(([kind, cfg]) => {
          const n = events.filter(e => e.kind === kind).length;
          const Icon = cfg.icon;
          return (
            <div key={kind} className="border border-white/10 p-3" data-testid={`timeline-kpi-${kind}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: cfg.color }}>
                <Icon size={12}/> {cfg.label}
              </div>
              <div className="font-display text-2xl tabular mt-1">{n}</div>
            </div>
          );
        })}
      </div>

      {/* Horizontal ribbon */}
      <div className="mt-8 border border-white/10 overflow-x-auto" data-testid="timeline-ribbon">
        <div className="flex divide-x divide-white/5 min-h-[280px]">
          {loading && !buckets.length && <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>}
          {!loading && !buckets.length && <div className="p-6 text-zinc-500 text-sm">Nothing on the timeline yet.</div>}
          {buckets.map(([hour, list]) => (
            <div key={hour} className="min-w-[220px] p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 pb-2 border-b border-white/5 sticky top-0 bg-[#0A0A0A]">
                {hour.replace("T"," · ")}:00 · {list.length}
              </div>
              <div className="mt-3 space-y-2">
                {list.slice(0, 30).map((e, i) => {
                  const cfg = KIND[e.kind] || { label: e.kind, color: "#71717A", icon: ArrowUpRight };
                  const Icon = cfg.icon;
                  return (
                    <div key={e.target_id + "-" + i} className="border-l-2 pl-2 py-1" style={{ borderColor: cfg.color }} data-testid={`timeline-event-${e.kind}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={{ color: cfg.color }}>
                        <Icon size={10}/> {(e.at||"").slice(11,16)}
                      </div>
                      <div className="text-xs mt-0.5 text-zinc-200 line-clamp-2">
                        {e.title}
                        {e.meta?.polling_unit && <span className="text-zinc-500"> · {e.meta.polling_unit}</span>}
                        {e.meta?.severity && <span className="text-zinc-500"> · <span className="uppercase">{e.meta.severity}</span></span>}
                        {e.meta?.status && <span className="text-zinc-500"> · {e.meta.status}</span>}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">{(e.actor||"").split("@")[0]}</div>
                      {e.meta?.message && <div className="text-[10px] text-zinc-400 line-clamp-2 mt-0.5">"{e.meta.message}"</div>}
                      {e.kind?.startsWith("result.") && e.target_id && !String(e.target_id).startsWith("live-") && (
                        <button
                          onClick={() => setPreviewId(e.target_id)}
                          data-testid={`timeline-view-sheet-${e.target_id}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-white/15 hover:bg-white/10 text-zinc-300">
                          <Eye size={10}/> View sheet
                        </button>
                      )}
                    </div>
                  );
                })}
                {list.length > 30 && <div className="text-[10px] text-zinc-500">+ {list.length - 30} more…</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail list */}
      <div className="mt-6 border border-white/10 data-table">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <div className="col-span-2">Time</div>
          <div className="col-span-2">Kind</div>
          <div className="col-span-4">Details</div>
          <div className="col-span-3">Actor</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        <div className="divide-y divide-white/5 max-h-[50vh] overflow-y-auto" data-testid="timeline-table">
          {filtered.slice(0, 200).map((e, i) => {
            const cfg = KIND[e.kind] || { label: e.kind, color: "#71717A", icon: ExternalLink };
            const Icon = cfg.icon;
            return (
              <div key={e.target_id + "-row-" + i} className="grid grid-cols-12 px-4 py-2.5 items-center text-xs hover:bg-white/[0.02]">
                <div className="col-span-2 tabular text-zinc-400">{fmtTime(e.at)}</div>
                <div className="col-span-2 flex items-center gap-2" style={{ color: cfg.color }}><Icon size={12}/> {cfg.label}</div>
                <div className="col-span-4 truncate text-zinc-200">
                  {e.title}
                  {e.meta?.polling_unit && <span className="text-zinc-500"> · {e.meta.polling_unit}</span>}
                  {e.meta?.title && <span className="text-zinc-500"> · {e.meta.title}</span>}
                  {e.meta?.message && <span className="text-zinc-500"> · "{e.meta.message}"</span>}
                </div>
                <div className="col-span-3 truncate text-zinc-400">{e.actor}</div>
                <div className="col-span-1 text-right">
                  {e.kind?.startsWith("result.") && e.target_id && !String(e.target_id).startsWith("live-") ? (
                    <button
                      onClick={() => setPreviewId(e.target_id)}
                      data-testid={`timeline-row-view-sheet-${e.target_id}`}
                      title="Preview EC8A sheet"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-white/15 hover:bg-white/10 text-zinc-300">
                      <Eye size={10}/> Sheet
                    </button>
                  ) : (
                    <span className="text-zinc-600">{(e.at||"").slice(0,10)}</span>
                  )}
                </div>
              </div>
            );
          })}
          {!filtered.length && !loading && <div className="p-6 text-zinc-500 text-sm">Nothing here.</div>}
        </div>
      </div>

      {previewId && <SheetPreviewModal resultId={previewId} onClose={() => setPreviewId(null)} />}
    </div>
  );
}
